/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDInput from 'components/MDInput';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import './PointOfSale.css';

function PosQuantity({ quantity, setQuantity, setBlockScan, callback }) {
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const txtQuantity = useRef(null);
  const btnQuantity = useRef(null);

  const open = () => {
    setQuantity('');
    setModalOpen(true);
    setBlockScan(true);
    // console.log(`open=${quantity}`);

    setTimeout(function () {
      txtQuantity.current.focus();
    }, 100);
  };

  const changeNumber = (num) => {
    let value = quantity;

    if (num === '-') {
      value = value.substr(0, value.length - 1);
    } else {
      value = value.concat(num);
    }

    setQuantity(value);
  };

  const send = () => {
    setModalOpen(false);
    setBlockScan(false);
    callback(quantity);

    setTimeout(function () {
      btnQuantity.current.blur();
    }, 100);
  };

  const close = () => {
    setModalOpen(false);
    setBlockScan(false);

    setTimeout(function () {
      btnQuantity.current.blur();
    }, 100);
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const btnNumPad = (num) => {
    return (
      <MDButton
        variant='text'
        color='info'
        size='large'
        sx={{ lineHeight: 4, fontSize: 20 }}
        fullWidth
        onClick={() => changeNumber(num)}
      >
        {num}
      </MDButton>
    );
  };

  const renderNumpad = () => {
    return (
      <MDBox p={2}>
        <table width='100%'>
          <tbody>
            <tr>
              <td colSpan={3}>
                <MDBox
                  noValidate
                  component='form'
                  role='form'
                  onSubmit={(e) => {
                    e.preventDefault();

                    if (quantity === '') return;
                    send();
                  }}
                >
                  <MDInput
                    onChange={(e) => setQuantity(e.target.value)}
                    value={quantity}
                    variant='outlined'
                    type='number'
                    fullWidth
                    InputProps={{
                      inputRef: txtQuantity,
                      inputProps: {
                        autoComplete: 'off',
                        placeholder: '0',
                        className: 'txt-quantity',
                      },
                    }}
                  />
                </MDBox>
              </td>
            </tr>
            <tr>
              <td width='25%'>{btnNumPad(7)}</td>
              <td width='25%'>{btnNumPad(8)}</td>
              <td width='25%'>{btnNumPad(9)}</td>
            </tr>
            <tr>
              <td>{btnNumPad(4)}</td>
              <td>{btnNumPad(5)}</td>
              <td>{btnNumPad(6)}</td>
            </tr>
            <tr>
              <td>{btnNumPad(1)}</td>
              <td>{btnNumPad(2)}</td>
              <td>{btnNumPad(3)}</td>
            </tr>
            <tr>
              <td>{btnNumPad('.')}</td>
              <td>{btnNumPad(0)}</td>
              <td>
                <MDButton
                  variant='text'
                  color='info'
                  size='large'
                  sx={{ lineHeight: 4 }}
                  fullWidth
                  onClick={() => changeNumber('-')}
                >
                  <Icon>backspace</Icon>
                </MDButton>
              </td>
            </tr>
          </tbody>
        </table>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={close}
      >
        <DialogTitle>
          แก้ไขปริมาณ
          <IconButton
            aria-label='close'
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {renderNumpad()}
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              <MDButton
                variant='text'
                color='error'
                onClick={() => {
                  setQuantity('');
                  callback('');
                  setModalOpen(false);
                  setBlockScan(false);
                }}
              >
                <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='text' color='success' onClick={send}>
                <Icon>check</Icon>&nbsp;{t(`alert.ok`)}
              </MDButton>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  useHotkeys('right', () => open());
  // useHotkeys("e", () => setQuantity(""));

  return (
    <MDBox>
      <Tooltip title='KEY : ➔'>
        <MDButton
          variant='outlined'
          color='dark'
          size='large'
          fullWidth
          ref={btnQuantity}
          onClick={open}
        >
          {quantity > 1 ? <span>x {quantity}</span> : <span>Quantity</span>}
        </MDButton>
      </Tooltip>
      {renderOpen()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

PosQuantity.propTypes = {
  quantity: PropTypes.string.isRequired,
  setQuantity: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
};

export default PosQuantity;
